import { useState } from 'react';
import clsx from 'clsx';
import { MdSchedule } from 'react-icons/md';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/Button';
import { datetimeFormat } from '@/utils/dateFormat';
import CalendarDatetimeMenu from '../../job_change_processes/components/parts/CalendarDatetimeMenu';
import useUpdateJobChangeProcess from '../../job_change_processes/api/useUpdateJobChangeProcess';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';
import type { JobChangeProcess } from '../../job_change_processes/types/JobChangeProcess';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobChangeProcess: JobChangeProcess;
  refetch: () => Promise<unknown>;
};

export default function DashboardScheduleAdjustDialog({
  open,
  onOpenChange,
  jobChangeProcess,
  refetch,
}: Props) {
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [isCalendarOpen, setIsCalendarOpen] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync } = useUpdateJobChangeProcess();

  const candidateName = jobChangeProcess.candidate_profile?.name ?? '';
  const companyName = jobChangeProcess.company?.name ?? '';

  const close = () => {
    onOpenChange(false);
    setDate(undefined);
    setIsCalendarOpen(true);
  };

  const onSubmit = async () => {
    if (!date) return;
    setIsSubmitting(true);
    try {
      await mutateAsync({
        jobChangeProcessId: jobChangeProcess.id,
        latestLogId: jobChangeProcess.latest_log?.id ?? 0,
        interviewed_at: date,
      });
      showSuccessToast('日時を登録しました');
    } catch {
      showErrorToast('日時の登録に失敗しました');
    } finally {
      await refetch();
      setIsSubmitting(false);
      close();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(next) : close())}>
      <DialogContent className="w-[372px] max-w-none rounded-sm">
        <div className="border-b border-solid border-border px-lg py-md">
          <p className="text-base font-bold text-main">
            {candidateName} × {companyName}
          </p>
        </div>
        <div className="flex flex-col gap-xs p-lg">
          <p className="text-base font-bold text-main">日時を選択してください</p>
          {isCalendarOpen ? (
            <CalendarDatetimeMenu
              currentDate={date}
              onCloseDateMenu={() => setIsCalendarOpen(false)}
              onSetDate={(d) => setDate(d)}
            />
          ) : (
            <button
              type="button"
              className={clsx(
                'flex h-[42px] w-[236px] items-center rounded-sm border border-solid border-border bg-white px-3'
              )}
              onClick={() => setIsCalendarOpen(true)}
            >
              <span className={clsx('text-sm', !date && 'text-placeholder')}>
                {date ? datetimeFormat(date) : 'YYYY/MM/DD 00:00'}
              </span>
              <MdSchedule size={20} className="ml-auto text-primary" aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="flex justify-end gap-md border-t border-solid border-border px-lg py-md">
          <Button color="secondary" size="md" onClick={close} disabled={isSubmitting}>
            キャンセル
          </Button>
          <Button color="primary" size="md" onClick={onSubmit} disabled={!date || isSubmitting}>
            決定
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
